import { useMemo } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/components/i18n/LanguageProvider";
import { DEFAULT_LOCALE } from "@/lib/i18n/locales";
import { uiText } from "@/lib/i18n/uiText";
import {
  clearTranslationFailure,
  reportTranslationFailure,
} from "@/lib/i18n/translationStatus";


/**
 * Runtime dynamic-content translation.
 *
 * The football data record is single-source (English). Prose fields (AI
 * analysis, match-preview copy, titles, descriptions, alt text) are translated
 * on demand by the `translate-content` edge function and cached in
 * `content_translations`. This hook is the client entry point.
 *
 * Behaviour:
 *   - English (root locale) renders the source instantly — no network round-trip.
 *   - Other locales fetch (and the edge function caches) the translation.
 *   - Loading/failure shows a localized status, never silent English prose.
 *
 * Numbers/scores/odds must NOT be passed here — only prose.
 */

export interface LocalizeItem {
  entityType: string;
  entityId: string;
  field: string;
  sourceText: string;
}

interface TranslateResult {
  entityType: string;
  entityId: string;
  field: string;
  text: string;
  fallback?: boolean;
  cached?: boolean;
}

// A short, stable hash of the source so a changed source invalidates the cache
// key (mirrors the edge function's source_hash without importing crypto here).
function quickHash(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

async function translateBatch(
  locale: string,
  items: LocalizeItem[],
): Promise<TranslateResult[]> {
  const { data, error } = await supabase.functions.invoke("translate-content", {
    body: { targetLocale: locale, items },
  });
  if (error || !data?.results) {
    // Mark source fallback explicitly; callers render a localized status.
    const reason =
      (data as { reason?: string } | null)?.reason ??
      (error as { message?: string } | null)?.message ??
      "translation_unavailable";
    reportTranslationFailure(String(reason), (data as { version?: string } | null)?.version);
    return items.map((it) => ({ ...it, text: it.sourceText, fallback: true }));
  }
  clearTranslationFailure();
  return data.results as TranslateResult[];
}


/**
 * Translate a single prose field. Returns the English source immediately on the
 * root locale; otherwise returns the cached/generated translation.
 */
export function useLocalizedContent(
  entityType: string,
  entityId: string | null | undefined,
  field: string,
  sourceText: string | null | undefined,
): { text: string; isLoading: boolean; fallback: boolean } {
  const { locale } = useLanguage();
  const src = sourceText ?? "";
  const enabled =
    locale !== DEFAULT_LOCALE && src.trim().length > 0;

  const query = useQuery({
    queryKey: ["localized-content", locale, entityType, entityId, field, quickHash(src)],
    queryFn: async () => {
      const [res] = await translateBatch(locale, [
        { entityType, entityId: entityId ?? "", field, sourceText: src },
      ]);
      return res;
    },
    enabled,
    staleTime: 24 * 60 * 60 * 1000, // translations rarely change
    gcTime: 24 * 60 * 60 * 1000,
  });

  if (!enabled) return { text: src, isLoading: false, fallback: false };
  return {
    text: query.data?.fallback
      ? uiText("Statlign translation is temporarily unavailable.")
      : query.data?.text ?? uiText("Translation loading…"),
    isLoading: query.isLoading,
    fallback: query.data?.fallback ?? false,
  };
}

/**
 * Translate many prose fields for one entity (or a list) in a single request.
 * Returns a lookup keyed by `${field}` (single entity) so callers can read each
 * translated field. English short-circuits to the sources.
 */
export function useLocalizedFields(
  entityType: string,
  entityId: string | null | undefined,
  fields: Record<string, string | null | undefined>,
): { values: Record<string, string>; isLoading: boolean } {
  const { locale } = useLanguage();

  const items = useMemo<LocalizeItem[]>(() => {
    if (!entityId) return [];
    return Object.entries(fields)
      .filter(([, v]) => (v ?? "").trim().length > 0)
      .map(([field, v]) => ({ entityType, entityId, field, sourceText: v as string }));
  }, [entityType, entityId, fields]);

  const enabled = locale !== DEFAULT_LOCALE && items.length > 0;

  const hashKey = useMemo(
    () => items.map((i) => `${i.field}:${quickHash(i.sourceText)}`).join("|"),
    [items],
  );

  const query = useQuery({
    queryKey: ["localized-fields", locale, entityType, entityId, hashKey],
    queryFn: () => translateBatch(locale, items),
    enabled,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });

  const values = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const [field, v] of Object.entries(fields)) out[field] = enabled && v ? uiText("Translation loading…") : v ?? "";
    if (enabled && query.data) {
      for (const r of query.data) out[r.field] = r.fallback ? uiText("Statlign translation is temporarily unavailable.") : r.text;
    }
    return out;
  }, [fields, enabled, query.data]);

  return { values, isLoading: enabled ? query.isLoading : false };
}

/**
 * Batch-translate a list of entities (e.g. prediction cards). Pass a flat list
 * of items; returns a Map keyed by `${entityId}::${field}`.
 */
export function useLocalizedList(
  items: LocalizeItem[],
): { map: Map<string, string>; isLoading: boolean } {
  const { locale } = useLanguage();
  const enabled = locale !== DEFAULT_LOCALE && items.length > 0;

  const hashKey = useMemo(
    () => items.map((i) => `${i.entityId}:${i.field}:${quickHash(i.sourceText)}`).join("|"),
    [items],
  );

  // Chunk into <=50 per request (edge-function cap).
  const chunks = useMemo(() => {
    const size = 50;
    const out: LocalizeItem[][] = [];
    for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
    return out;
  }, [items]);

  const results = useQueries({
    queries: chunks.map((chunk, idx) => ({
      queryKey: ["localized-list", locale, idx, hashKey],
      queryFn: () => translateBatch(locale, chunk),
      enabled,
      staleTime: 24 * 60 * 60 * 1000,
      gcTime: 24 * 60 * 60 * 1000,
    })),
  });

  const map = useMemo(() => {
    const m = new Map<string, string>();
    for (const it of items) m.set(`${it.entityId}::${it.field}`, enabled ? uiText("Translation loading…") : it.sourceText);
    if (enabled) {
      for (const q of results) {
        for (const r of q.data ?? []) m.set(`${r.entityId}::${r.field}`, r.fallback ? uiText("Statlign translation is temporarily unavailable.") : r.text);
      }
    }
    return m;
  }, [items, enabled, results]);

  const isLoading = enabled && results.some((q) => q.isLoading);
  return { map, isLoading };
}
